"use client";
import {
  Hero,
  InvestmentFilters,
  Pagination,
  PropertyFilters,
} from "@/components/bnp-ui";
import { HomeWrapper } from "@/components/layouts";
import { containerWidth } from "@/components/layouts/utils";
import { Button, Tabs } from "@/components/bnp-ui";
import {
  InvestmentCard,
  MapLocation,
  PropertyPhotoGrid,
} from "@/components/shared";
import { formatNumber } from "@/utils/formatNumber";
import parseHttpError from "@/utils/parseHttpError";
import { Modal } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import { InvestmentService } from "@/services";
import { useQuery } from "@tanstack/react-query";
import About from "@/components/pages/user/dashboard/invest-flow/About";
import Documents from "@/components/pages/user/dashboard/invest-flow/Documents";
import Updates from "@/components/pages/user/dashboard/invest-flow/Updates";
import Subscribe from "@/components/pages/user/dashboard/invest-flow/Subscribe";
import { getDevelopmentTypeName } from "@/components/shared/investment-card";
import {
  HomeIllustration,
  HomeOwner,
  LandIllustration,
  NoSubscriptions,
  WeeklyDealsHome,
} from "@/components/bnp-illustrations";
import useAuthToken from "@/hooks/useAuthToken";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import Link from "next/link";
import { SummaryLocation } from "@/components/bnp-icons";
import GoogleAd from "@/components/shared/GoogleAd";
import BannerAd from "@/components/shared/ads/BannerAd";
import HeaderAd from "@/components/shared/ads/HeaderAd";
import InFeedAd from "@/components/shared/ads/InFeedAd";
import SidebarAd from "@/components/shared/ads/SidebarAd";
import FooterAd from "@/components/shared/ads/FooterAd";
import { usePageAds } from "@/hooks/usePageAds";
import { AdPage } from "@/types/advertising";
import { set } from "react-hook-form";
import RALDRealtySection from "@/components/pages/managed-developments/RALDRealtySection";
import PartnerFilter from "@/components/pages/managed-developments/PartnerFilter";
import {
  PartnerFilter as PartnerFilterType,
  PartnerProperty,
} from "@/types/partner";
import { enhancePropertyData } from "@/utils/partnerBranding";

const Investments = () => {
  const [openedInvestmentIndex, setOpenedInvestmentIndex] = useState<
    number | null
  >(null);
  const [opened, { open, close }] = useDisclosure(false);
  const [activeTab, setActiveTab] = useState(0);
  const [timeLeft, setTimeLeft] = useState<any>("");
  const [investments, setInvestments] = useState<any>([]);
  const [investFlow, setInvestFlow] = useState(false);
  const { user, token } = useAuthToken();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;
  const router = useRouter();
  const { bannerAds, inFeedAds, sidebarAds, footerAds, headerAds } = usePageAds(
    AdPage.MANAGED_DEVELOPMENTS,
  );
  const [partnerFilter, setPartnerFilter] = useState<PartnerFilterType>({
    partners: ["rald-realty"],
    selectedPartner: undefined,
    isRALDRealtyOnly: false,
  });
  const [raldProperties, setRaldProperties] = useState<PartnerProperty[]>([]);

  const {
    state,
    city,
    bedroom_count,
    bathroom_count,
    developmentType,
    maxPrice,
    minPrice,
  } = router.query;

  const getInvestments = async () => {
    try {
      const queryParams = {
        ...(state && { state }),
        ...(city && { city }),
        ...(bedroom_count && { bedroom_count }),
        ...(bathroom_count && { bathroom_count }),
        ...(developmentType && { developmentType }),
        ...(maxPrice && { maxPrice }),
        ...(minPrice && { minPrice }),
        ...(partnerFilter.isRALDRealtyOnly && { isPartner: "true" }),
        pageNumber: currentPage,
        pageSize,
      };

      const hasQueryParams = Object.keys(queryParams).length > 0;
      const response = await InvestmentService.GetInvestmentOfferingsFilter(
        hasQueryParams ? queryParams : undefined,
      );
      // console.log(response?.data);
      return response?.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const {
    data: investmentsData,
    isLoading: investmentsIsLoading,
    isError: investmentsIsError,
    refetch: investmentsRefetch,
    isSuccess: investmentsIsSuccess,
  } = useQuery({
    queryKey: [
      "investments",
      state,
      city,
      bedroom_count,
      bathroom_count,
      developmentType,
      maxPrice,
      minPrice,
      pageSize,
      currentPage,
      partnerFilter.isRALDRealtyOnly,
    ],
    queryFn: getInvestments,
    staleTime: 0,
  });

  useEffect(() => {
    if (!investmentsData || !investmentsData.data) return;
    if (investmentsData) {
      setInvestments(investmentsData?.data);
    }

    const developmentData = investmentsData.data.map((development: any) => {
      console.log("Development", development);
      let updated = { ...development };

      if (development.developmentType === "off-plan-sale") {
        updated.totalInvestmentAmount =
          development.unitsAvailableForSubscription * development.amountPerUnit;
      }

      return updated;
    });

    setInvestments(developmentData);

    // Extract RALD REALTY properties for dedicated section
    const raldDevelopments = developmentData
      .filter(
        (dev: any) =>
          dev.partnerId === "rald-realty" || dev.partner === "rald-realty",
      )
      .slice(0, 6)
      .map((dev: any) => enhancePropertyData(dev, "rald-realty"));
    setRaldProperties(raldDevelopments);
  }, [investmentsData]);

  const handleOpenModal = (index: number) => {
    setOpenedInvestmentIndex(index);
    open();
  };

  const handleCloseModal = () => {
    setOpenedInvestmentIndex(null);
    setActiveTab(0);
    setInvestFlow(false);
    close();
  };

  const tabs = [
    {
      label: "About",
      //@ts-ignore
      content: <About {...investments[openedInvestmentIndex]} />,
    },

    {
      label: "Documents",
      content: (
        <Documents
          //@ts-ignore
          investmentId={investments[openedInvestmentIndex]?.id}
        />
      ),
    },

    {
      label: "Location",
      content: (
        <div className="flex flex-col gap-4">
          <h2 className="font-bold text-base">Location</h2>

          <div className="flex items-center gap-2 text-bnp-cyan">
            <SummaryLocation />

            <p className="font-medium text-base">
              {/* @ts-ignore */}
              {investments[openedInvestmentIndex]?.proposedLocation}
            </p>
          </div>

          <MapLocation
            //@ts-ignore
            address={investments[openedInvestmentIndex]?.proposedLocation}
          />
        </div>
      ),
    },

    {
      label: "Updates",
      content: (
        //@ts-ignore
        <Updates investmentId={investments[openedInvestmentIndex]?.id} />
      ),
    },
  ];

  useEffect(() => {
    if (openedInvestmentIndex !== null) {
      const offerEndDate = new Date(
        investments[openedInvestmentIndex]?.offerEndsDate,
      );

      const calculateTimeLeft = () => {
        const now = new Date();
        const difference = offerEndDate.getTime() - now.getTime();

        if (difference > 0) {
          const days = Math.floor(difference / (1000 * 60 * 60 * 24));
          const hours = Math.floor(
            (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
          );
          const minutes = Math.floor(
            (difference % (1000 * 60 * 60)) / (1000 * 60),
          );

          return { days, hours, minutes };
        }

        return { days: 0, hours: 0, minutes: 0 };
      };

      setTimeLeft(calculateTimeLeft());

      const timer = setInterval(() => {
        const newTimeLeft = calculateTimeLeft();
        setTimeLeft(newTimeLeft);

        if (
          newTimeLeft.days === 0 &&
          newTimeLeft.hours === 0 &&
          newTimeLeft.minutes === 0
        ) {
          clearInterval(timer);
        }
      }, 60000);

      return () => clearInterval(timer);
    }
  }, [openedInvestmentIndex]);

  const handlePageChange = (pageNumber: number) => {
    const newQuery = {
      ...router.query,
      page: pageNumber.toString(),
    };

    router.push({
      pathname: router.pathname,
      query: newQuery,
    });
  };

  const handlePartnerFilterChange = (filter: PartnerFilterType) => {
    setPartnerFilter(filter);
  };

  const handlePropertyClick = (property: PartnerProperty) => {
    if (property.id) {
      router.push(`/investment/${property.id}`);
    }
  };

  console.log("Investments:", investments);

  const investmentStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: investments
      .slice(0, 10)
      .map((investment: any, index: number) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "FinancialProduct",
          name: investment?.description || "Investment Opportunity",
          description: getDevelopmentTypeName(investment?.developmentType),
          url: `https://www.findnigeriaproperties.com/investment/${investment?.id}`,
          image:
            investment?.image_url?.[0] ||
            "https://www.findnigeriaproperties.com/images/default-investment.jpg",
          offers: {
            "@type": "Offer",
            price: investment?.totalInvestmentAmount || 0,
            priceCurrency: "NGN",
          },
        },
      })),
  };

  return (
    <>
      {investments.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(investmentStructuredData),
          }}
        />
      )}
      <HomeWrapper
        footerSlot={footerAds[0] ? <FooterAd ad={footerAds[0]} /> : undefined}
      >
        <div className={containerWidth}>
          <div className="relative flex flex-col items-center w-[100%] lg:w-[1104px]">
            {/* RALD REALTY Section */}
            {raldProperties.length > 0 && !investmentsIsLoading && (
              <div className="w-full">
                <RALDRealtySection
                  properties={raldProperties}
                  onPropertyClick={handlePropertyClick}
                  loading={investmentsIsLoading}
                />
              </div>
            )}

            <div className="flex justify-center flex-wrap gap-5 w-[100%] mt-[40px] lg:mt-[25px]  mb-20">
              <div className="w-[100%]  flex justify-center gap-[50px] relative mt-8 ">
                {investmentsIsLoading ? (
                  <div className="flex flex-col justify-center items-center  w-[100%] lg:w-full h-fit">
                    <div className="w-[100%] lg:w-fit h-fit">
                      <Image
                        //@ts-ignore
                        src={"/assets/images/property-default.svg"}
                        width={200}
                        height={200}
                        alt={"property"}
                        className="w-[400px] h-[400px] object-cover animate-pulse"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="w-[100%] lg:w-fit">
                    {investments.length > 0 ? (
                      <div className="p-2 lg:p-0  properties-grid">
                        {investments.map((investment: any, index: number) => {
                          return (
                            <React.Fragment key={index}>
                              <Link
                                href={`/investment/${investment?.id}`}
                                className="w-[100%] lg:w-fit h-fit cursor-pointer hover:scale-105 transition-all ease-linear delay-100"
                              >
                                <div
                                  className="w-[100%] lg:w-fit h-fit"
                                  // onClick={() => handleOpenModal(index)}
                                >
                                  <InvestmentCard
                                    title={investment?.description}
                                    amountRequired={
                                      investment?.totalInvestmentAmount
                                    }
                                    propertyStats={{
                                      amountRaised:
                                        investment?.amountAlreadyRaised,
                                      subscriberCount:
                                        investment?.subscriberCount,
                                      expectedReturns:
                                        investment?.expectedReturns,
                                      fundedDate: investment?.fundedDate,
                                      minimumBuyIn:
                                        investment?.minimumInvestment,
                                      preferredListingEndDate:
                                        investment?.preferredListingEndDate,
                                      deliveryDate: investment?.deliveryDate,
                                      targetRedemptionDate:
                                        investment?.targetRedemptionDate,
                                      developmentType:
                                        investment?.developmentType,
                                      image_url: investment?.image_url,
                                      amountPerUnit: investment?.amountPerUnit,
                                      subsequentMonthlyDeposit:
                                        investment?.subsequentMonthlyDeposit,
                                      initialDeposit:
                                        investment?.initialDeposit,
                                    }}
                                    currencyPrefix="₦"
                                  />
                                </div>
                              </Link>
                              {/* In-feed ad after every 6th investment */}
                              {(index + 1) % 6 === 0 &&
                                inFeedAds[
                                  Math.floor(index / 6) % inFeedAds.length
                                ] && (
                                  <InFeedAd
                                    ad={
                                      inFeedAds[
                                        Math.floor(index / 6) % inFeedAds.length
                                      ]
                                    }
                                    className="full-width"
                                  />
                                )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="w-[100%] justify-center items-center flex flex-col">
                        <NoSubscriptions />
                        <p className="text-grey-7 lg:w-[408px] text-center mt-4 text-lg">
                          No active offerings yet
                        </p>
                        <Button
                          className="bg-bnp-dark  text-white text-sm font-medium mt-4 w-[157px] h-[45px] rounded-lg"
                          onClick={() => {
                            router.replace("/managed-developments", undefined, {
                              shallow: true,
                            });
                            investmentsRefetch();
                          }}
                        >
                          Explore properties
                        </Button>
                      </div>
                    )}
                    <div className=" w-[100%] lg:w-[800px]  mx-auto flex justify-center my-8">
                      <GoogleAd
                        adSlot={process.env.NEXT_PUBLIC_AD_SLOT_BANNER || ""}
                        adFormat="horizontal"
                        fullWidthResponsive={true}
                        className="w-full"
                      />
                    </div>

                    <Pagination
                      metadata={investmentsData?.metadata}
                      currentPage={currentPage}
                      pageSize={pageSize}
                      onPageChange={handlePageChange}
                      className="w-full mt-8"
                    />
                  </div>
                )}

                {investments.length > 0 && !investmentsIsLoading && (
                  <div className="hidden lg:block lg:min-w-[300px] lg:max-w-[300px] lg:top-20 space-y-4">
                    <PartnerFilter
                      filter={partnerFilter}
                      onFilterChange={handlePartnerFilterChange}
                      availablePartners={["rald-realty"]}
                    />
                    <InvestmentFilters />
                    <div className="sticky top-20 z-10 mt-4">
                      {/* Internal Sidebar Ad */}
                      {sidebarAds[0] && (
                        <div className="mb-4">
                          <SidebarAd ad={sidebarAds[0]} />
                        </div>
                      )}
                      <GoogleAd
                        adSlot={process.env.NEXT_PUBLIC_AD_SLOT_SIDEBAR || ""}
                        adFormat="rectangle"
                        className="w-full"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {bannerAds[0] && (
            <div className="w-full max-w-[1280px] mx-auto px-4 py-6">
              <BannerAd ad={bannerAds[0]} />
            </div>
          )}
        </div>
      </HomeWrapper>
    </>
  );
};

export default Investments;
